import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogUrl?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown>;
}

export default function SEO({ title, description, keywords, canonicalUrl, ogTitle, ogUrl, ogType, jsonLd }: SEOProps) {
  const location = useLocation();

  useEffect(() => {
    document.title = title;

    const setMetaTag = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMetaTag('description', description);
    if (keywords) setMetaTag('keywords', keywords);

    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonicalUrl);
    }

    if (ogTitle) setMetaTag('og:title', ogTitle, true);
    if (ogUrl) setMetaTag('og:url', ogUrl, true);
    if (ogType) setMetaTag('og:type', ogType, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:locale', 'zh_CN', true);

    setMetaTag('twitter:card', 'summary_large_image');
    if (ogTitle) setMetaTag('twitter:title', ogTitle);
    setMetaTag('twitter:description', description);

    if (jsonLd) {
      let script = document.querySelector('script[type="application/ld+json"]');
      if (!script) {
        script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    }

    return () => {
      const jsonLdScript = document.querySelector('script[type="application/ld+json"]');
      if (jsonLdScript) jsonLdScript.remove();
    };
  }, [title, description, keywords, canonicalUrl, ogTitle, ogUrl, ogType, jsonLd, location.pathname]);

  return null;
}